import React, { FC, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { CreateSurveyTemplateQuestionsDto } from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { EyeIcon } from '../../../_core/components/icons';
import { useOpenSurveyModal } from '../../hooks/modal-forms/use-open-view-modal.hook';
import { SurveyTemplateQuestionsArray } from '../modal-forms/questions-array.component';
import mongoose from 'mongoose';

export type EnglishSurveyTemplateFormProps = {
	currentLanguage: string;
	defaultValues?: any;
	setAllFormsValues?: any;
	setDisabledTemplateForm: any;
};
export type EnglishSurveyTemplateFormHandle = {
	isDirty: boolean;
};

export const EnglishSurveyTemplateForm = forwardRef<EnglishSurveyTemplateFormHandle, EnglishSurveyTemplateFormProps>(
	(props, ref) => {
		const [t] = useTranslation();
		const isFirstRender = useRef(true);

		const { defaultValues, currentLanguage, setAllFormsValues, setDisabledTemplateForm } = props;

		const resolver = classValidatorResolver(CreateSurveyTemplateQuestionsDto);

		const methods = useForm({
			defaultValues,
			resolver,
			mode: 'all',
		});

		const {
			getValues,
			control,
			reset,
			formState: { isValid, isDirty, isSubmitting, isSubmitted },
		} = methods;

		const watchedItems = useWatch({
			name: 'questions',
			control,
		});

		useEffect(() => {
			if (isDirty) {
				setAllFormsValues((prev: any) =>
					Object.keys(prev).reduce((allObj: any, languageName: string) => {
						if (languageName === 'Template') {
							allObj[languageName] = { ...prev[languageName], questions: getValues('questions') };

							return allObj;
						}
						let isFinished = true;
						const newQuestions = getValues('questions')?.map((question: any) => {
							const existTranslatedQuestion = prev[languageName].questions.find(
								(translatedQuestion: any) => translatedQuestion.questionId?.toString() === question._id?.toString()
							);
							if (existTranslatedQuestion) {
								const newOptions = question?.options?.map((option: any) => {
									const existTranslatedOption = existTranslatedQuestion?.options?.find(
										(translatedOption: any) =>
											translatedOption.optionId && translatedOption.optionId?.toString() === option._id?.toString()
									);
									if (existTranslatedOption) {
										if (
											existTranslatedOption.text !== option.text ||
											!option.text ||
											!existTranslatedOption.translatedText
										) {
											isFinished = false;
										}
										return {
											...option,
											_id: existTranslatedOption._id,
											optionId: existTranslatedOption.optionId,
											translatedText:
												existTranslatedOption.text === option.text && option.text
													? existTranslatedOption.translatedText
													: '',
										};
									}
									isFinished = false;
									return {
										...option,
										_id: new mongoose.Types.ObjectId().toString(),
										optionId: option._id,
										translatedText: '',
									};
								});
								if (
									existTranslatedQuestion.text !== question.text ||
									!question.text ||
									!existTranslatedQuestion.translatedText
								) {
									isFinished = false;
								}
								return {
									...question,
									_id: existTranslatedQuestion._id,
									questionId: existTranslatedQuestion.questionId,
									translatedText:
										existTranslatedQuestion.text === question.text && question.text
											? existTranslatedQuestion.translatedText
											: '',
									options: newOptions,
								};
							}
							isFinished = false;
							return {
								...question,
								_id: new mongoose.Types.ObjectId().toString(),
								questionId: question._id,
								translatedText: '',
								options: question?.options?.map((option: any) => ({
									...option,
									_id: new mongoose.Types.ObjectId().toString(),
									optionId: option._id,
									translatedText: '',
								})),
							};
						});
						allObj[languageName] = {
							...prev[languageName],
							isFinished,
							questions: newQuestions,
						};

						return allObj;
					}, {})
				);
			}
		}, [watchedItems, isDirty]);

		useEffect(() => {
			if (!getValues('questions')?.length) {
				reset(defaultValues);
			}
		}, [defaultValues, reset]);

		useImperativeHandle(ref, () => ({
			isDirty,
			isSubmitting,
			resetForm: () => reset(defaultValues),
		}));

		useEffect(() => {
			setDisabledTemplateForm(!isValid && (isDirty || isSubmitted));
		}, [isValid, isDirty, isSubmitted]);

		const currentQuestions = getValues('questions');

		const openSurveyModal = useOpenSurveyModal({
			modalTitle: '',
			questions: currentQuestions,
		});

		return (
			<FormProvider {...methods}>
				<Box component="form" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							borderBottom: '1px solid lightgrey',
							paddingBottom: 3,
						}}
					>
						<Typography sx={{ fontSize: '24px', fontWeight: 400 }}>{`${currentLanguage} language`}</Typography>
						<CoreButton
							variant="info-light"
							tooltipTitle={t('buttons.tooltips.survey-preview')}
							circle
							size="large"
							onClick={openSurveyModal}
						>
							<EyeIcon sx={{ fontSize: 30 }} />
						</CoreButton>
					</Box>
					<SurveyTemplateQuestionsArray />
				</Box>
			</FormProvider>
		);
	}
);
